
import React, { useState, useEffect } from 'react';
import MainLayout from '../components/Layout/MainLayout';
import ConsumptionChart from '../components/Charts/ConsumptionChart';
import { TrendingUp, Zap, Calendar, PieChart, Download, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Sector, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';
import { usePricingRate } from '@/hooks/usePricing';
import { getUserApartmentId } from '@/lib/apartment';

interface LoadProfileDataPoint {
  time: string;
  consumption: number;
  dateRange?: string; // For week/month views to show actual date ranges
}

const ConsumptionEvolution: React.FC = () => {
  const { user } = useAuth();
  const [pieRotation, setPieRotation] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loadProfilePeriod, setLoadProfilePeriod] = useState<'day' | 'week' | 'month'>('day');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [realLoadProfileData, setRealLoadProfileData] = useState<Record<'day' | 'week' | 'month', LoadProfileDataPoint[]>>({
    day: [],
    week: [],
    month: []
  });
  // Store raw meter readings for accurate export
  const [rawMeterReadings, setRawMeterReadings] = useState<{
    day: Array<{ reading_datetime: string; instantaneous_power_w: number }>;
    week: Array<{ reading_datetime: string; instantaneous_power_w: number }>;
    month: Array<{ reading_datetime: string; instantaneous_power_w: number }>;
  }>({ day: [], week: [], month: [] });
  const [loading, setLoading] = useState(false);
  // Consumption data for all periods (for the bar chart)
  const [consumptionByPeriod, setConsumptionByPeriod] = useState<{
    day: Array<{ date: string; kwh: number; cost: number }>;
    week: Array<{ date: string; kwh: number; cost: number }>;
    month: Array<{ date: string; kwh: number; cost: number }>;
    year: Array<{ date: string; kwh: number; cost: number }>;
  }>({ day: [], week: [], month: [], year: [] });
  const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [chartWindowStart, setChartWindowStart] = useState(0); // For pagination through data points
  const [consumptionStats, setConsumptionStats] = useState({
    averageDaily: 0,
    thisMonth: 0,
    vsLastMonth: 0
  });
  const ratePerKwh = usePricingRate(0);

  // Enhanced pie data with A/C included
  const pieData = [
    { name: 'Lighting', value: 25, color: '#3B82F6', icon: '💡', description: 'LED and traditional bulbs', pattern: '⚬⚬⚬' },
    { name: 'Heating/AC', value: 30, color: '#EF4444', icon: '❄️', description: 'Air conditioning and heating', pattern: '▬▬▬' },
    { name: 'Appliances', value: 20, color: '#10B981', icon: '🏠', description: 'Kitchen and home appliances', pattern: '◦◦◦' },
    { name: 'Electronics', value: 15, color: '#F59E0B', icon: '📺', description: 'TV, computers, and gadgets', pattern: '▪▪▪' },
    { name: 'Other', value: 10, color: '#8B5CF6', icon: '⚡', description: 'Miscellaneous devices', pattern: '◊◊◊' }
  ];

  // Store meter ID for reuse
  const [meterId, setMeterId] = useState<string | null>(null);
  const [meterStatus, setMeterStatus] = useState<'loading' | 'found' | 'no_apartment' | 'no_meter'>('loading');

  // Fetch meter ID once on mount
  useEffect(() => {
    const fetchMeterId = async () => {
      if (!user?.id) {
        console.log('[LoadProfile] No user ID available');
        return;
      }

      setMeterStatus('loading');

      try {
        console.log('[LoadProfile] Fetching meter for user:', user.id);

        // Resolve apartment (supports cotenants via user_apartments fallback)
        const apartmentId = await getUserApartmentId(user.id);

        if (!apartmentId) {
          console.log('[LoadProfile] User has no apartment assigned');
          setMeterStatus('no_apartment');
          return;
        }

        console.log('[LoadProfile] User apartment:', apartmentId);

        const { data: meterData, error: meterError } = await supabase
          .from('smart_meters')
          .select('id')
          .eq('apartment_id', apartmentId)
          .maybeSingle();

        if (meterError) {
          console.error('[LoadProfile] Error fetching meter:', meterError);
          return;
        }

        if (meterData) {
          console.log('[LoadProfile] Found meter:', meterData.id);
          setMeterId(meterData.id);
          setMeterStatus('found');
        } else {
          console.log('[LoadProfile] No meter found for apartment');
          setMeterStatus('no_meter');
        }
      } catch (error) {
        console.error('[LoadProfile] Error fetching meter ID:', error);
      }
    };

    fetchMeterId();
  }, [user?.id]);

  // Fetch only the currently selected load profile period
  useEffect(() => {
    const fetchLoadProfileData = async () => {
      if (!meterId) {
        console.log('[LoadProfile] Skipping fetch - no meter ID');
        return;
      }

      setLoading(true);
      try {
        const endOfSelectedDate = new Date(selectedDate);
        endOfSelectedDate.setHours(23, 59, 59, 999);

        // Calculate date range based on selected period
        const periodDays = { day: 1, week: 7, month: 30 };
        const startDate = new Date(endOfSelectedDate.getTime() - periodDays[loadProfilePeriod] * 24 * 60 * 60 * 1000);

        console.log(`[LoadProfile] Fetching ${loadProfilePeriod} data:`, {
          meterId,
          startDate: startDate.toISOString(),
          endDate: endOfSelectedDate.toISOString(),
          selectedDate: selectedDate.toISOString()
        });

        // Fetch all readings in batches (Supabase default limit is 1000)
        const allReadings: Array<{ reading_datetime: string; instantaneous_power_w: number }> = [];
        let offset = 0;
        const batchSize = 1000;

        while (true) {
          const { data: batch, error: readingsError } = await supabase
            .from('meter_readings')
            .select('reading_datetime, instantaneous_power_w')
            .eq('meter_id', meterId)
            .gte('reading_datetime', startDate.toISOString())
            .lte('reading_datetime', endOfSelectedDate.toISOString())
            .order('reading_datetime', { ascending: true })
            .range(offset, offset + batchSize - 1);

          if (readingsError) {
            console.error('[LoadProfile] Error fetching readings:', readingsError);
            setLoading(false);
            return;
          }

          if (!batch || batch.length === 0) break;
          allReadings.push(...batch);
          if (batch.length < batchSize) break;
          offset += batchSize;
        }

        const readings = allReadings;

        if (readings.length === 0) {
          console.log('[LoadProfile] No readings found for period');
          setRealLoadProfileData(prev => ({ ...prev, [loadProfilePeriod]: [] }));
          setRawMeterReadings(prev => ({ ...prev, [loadProfilePeriod]: [] }));
          setLoading(false);
          return;
        }

        console.log(`[LoadProfile] Found ${readings.length} readings`);

        // Store raw readings for export
        setRawMeterReadings(prev => ({ ...prev, [loadProfilePeriod]: readings }));

        // Transform based on period
        let transformedData: LoadProfileDataPoint[] = [];

        if (loadProfilePeriod === 'day') {
          // Show each individual reading
          transformedData = readings.map(reading => ({
            time: format(new Date(reading.reading_datetime), 'HH:mm'),
            consumption: Math.round(Number(reading.instantaneous_power_w))
          }));
        } else if (loadProfilePeriod === 'week') {
          // Group by 4-hour intervals with day prefix
          const intervalData = new Map<string, { total: number; count: number; sortKey: number }>();
          readings.forEach(reading => {
            const date = new Date(reading.reading_datetime);
            const dayShort = format(date, 'EEE');
            const hour = date.getHours();
            const intervalStart = Math.floor(hour / 4) * 4;
            const intervalEnd = intervalStart + 4;
            const key = `${dayShort} ${intervalStart.toString().padStart(2, '0')}-${intervalEnd.toString().padStart(2, '0')}`;
            const sortKey = date.getTime();
            const power = Number(reading.instantaneous_power_w);

            if (!intervalData.has(key)) {
              intervalData.set(key, { total: 0, count: 0, sortKey });
            }
            const data = intervalData.get(key)!;
            data.total += power;
            data.count += 1;
            if (sortKey < data.sortKey) data.sortKey = sortKey;
          });

          transformedData = Array.from(intervalData.entries())
            .sort((a, b) => a[1].sortKey - b[1].sortKey)
            .map(([time, data]) => ({
              time,
              consumption: Math.round(data.total / data.count)
            }));
        } else {
          // Month: group by day
          const dailyData = new Map<string, { total: number; count: number; sortKey: number }>();
          readings.forEach(reading => {
            const date = new Date(reading.reading_datetime);
            const dayKey = format(date, 'MMM d');
            const sortKey = date.getTime();
            const power = Number(reading.instantaneous_power_w);

            if (!dailyData.has(dayKey)) {
              dailyData.set(dayKey, { total: 0, count: 0, sortKey });
            }
            const data = dailyData.get(dayKey)!;
            data.total += power;
            data.count += 1;
            if (sortKey < data.sortKey) data.sortKey = sortKey;
          });

          transformedData = Array.from(dailyData.entries())
            .sort((a, b) => a[1].sortKey - b[1].sortKey)
            .map(([time, data]) => ({
              time,
              consumption: Math.round(data.total / data.count)
            }));
        }

        setRealLoadProfileData(prev => ({ ...prev, [loadProfilePeriod]: transformedData }));
        console.log(`Loaded ${loadProfilePeriod} load profile: ${transformedData.length} points`);
      } catch (error) {
        console.error('Error fetching load profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLoadProfileData();
  }, [meterId, loadProfilePeriod, selectedDate]);

  // Fetch consumption data separately (90 days instead of 365)
  useEffect(() => {
    const fetchConsumptionData = async () => {
      if (!meterId) return;

      try {
        const now = new Date();
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

        // Supabase has a project-level max-rows limit (default 1000)
        // Fetch data in batches to get all readings
        const allReadings: Array<{ reading_datetime: string; forward_energy_kwh: number }> = [];
        let offset = 0;
        const batchSize = 1000;

        while (true) {
          const { data: batch } = await supabase
            .from('meter_readings')
            .select('reading_datetime, forward_energy_kwh')
            .eq('meter_id', meterId)
            .gte('reading_datetime', ninetyDaysAgo.toISOString())
            .order('reading_datetime', { ascending: true })
            .range(offset, offset + batchSize - 1);

          if (!batch || batch.length === 0) break;
          allReadings.push(...batch);
          if (batch.length < batchSize) break;
          offset += batchSize;
        }

        const rawReadings = allReadings;
        if (!rawReadings || rawReadings.length < 2) return;

        const dailyGroups = new Map<string, number>();
        const weeklyGroups = new Map<string, number>();
        const monthlyGroups = new Map<string, number>();
        const yearlyGroups = new Map<string, number>();

        for (let i = 1; i < rawReadings.length; i++) {
          const prevForward = Number(rawReadings[i - 1].forward_energy_kwh || 0);
          const currForward = Number(rawReadings[i].forward_energy_kwh || 0);
          const delta = currForward - prevForward;
          // Skip negative deltas and unreasonably large jumps (>30 kWh = corrupt data)
          // 30 kWh in ~10 min = 180 kW avg, far exceeds any residential/commercial use
          if (delta < 0 || delta > 30) continue;
          const kwh = delta;

          const date = new Date(rawReadings[i].reading_datetime);

          const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          dailyGroups.set(dayKey, (dailyGroups.get(dayKey) || 0) + kwh);

          const dayOfWeek = date.getDay();
          const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() + mondayOffset);
          const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
          weeklyGroups.set(weekKey, (weeklyGroups.get(weekKey) || 0) + kwh);

          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyGroups.set(monthKey, (monthlyGroups.get(monthKey) || 0) + kwh);

          const yearKey = `${date.getFullYear()}`;
          yearlyGroups.set(yearKey, (yearlyGroups.get(yearKey) || 0) + kwh);
        }

        const toDataArray = (groups: Map<string, number>) =>
          Array.from(groups.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, kwh]) => ({
              date,
              kwh: Math.round(kwh * 100) / 100,
              cost: Math.round(kwh * ratePerKwh * 100) / 100
            }));

        const dailyData = toDataArray(dailyGroups);
        const weeklyData = toDataArray(weeklyGroups);
        const monthlyData = toDataArray(monthlyGroups);
        const yearlyData = toDataArray(yearlyGroups);

        setConsumptionByPeriod({
          day: dailyData.slice(-30),
          week: weeklyData.slice(-12),
          month: monthlyData.slice(-12),
          year: yearlyData
        });

        // Calculate stats
        const currentMonth = monthlyData[monthlyData.length - 1];
        const lastMonth = monthlyData[monthlyData.length - 2];
        const daysInCurrentMonth = new Date().getDate();
        const averageDaily = currentMonth ? currentMonth.kwh / daysInCurrentMonth : 0;
        const vsLastMonth = lastMonth && currentMonth && lastMonth.kwh > 0
          ? ((currentMonth.kwh - lastMonth.kwh) / lastMonth.kwh) * 100
          : 0;

        setConsumptionStats({
          averageDaily,
          thisMonth: currentMonth?.kwh || 0,
          vsLastMonth
        });
      } catch (error) {
        console.error('Error fetching consumption data:', error);
      }
    };

    fetchConsumptionData();
  }, [meterId, ratePerKwh]);

  // Use real data if available, otherwise fall back to default empty structure
  const loadProfileData = realLoadProfileData;

  // Reset chart window when period changes
  useEffect(() => {
    setChartWindowStart(0);
  }, [loadProfilePeriod, selectedDate]);

  // Chart window settings - show limited data points at a time for readability
  const CHART_WINDOW_SIZE = loadProfilePeriod === 'day' ? 12 : loadProfilePeriod === 'week' ? 14 : 15;
  const fullData = loadProfileData[loadProfilePeriod] || [];
  const totalDataPoints = fullData.length;
  const maxWindowStart = Math.max(0, totalDataPoints - CHART_WINDOW_SIZE);

  // Get windowed data for display
  const currentData = fullData.slice(chartWindowStart, chartWindowStart + CHART_WINDOW_SIZE);

  // Navigation handlers
  const canGoBack = chartWindowStart > 0;
  const canGoForward = chartWindowStart < maxWindowStart;
  const handlePrevious = () => setChartWindowStart(Math.max(0, chartWindowStart - CHART_WINDOW_SIZE));
  const handleNext = () => setChartWindowStart(Math.min(maxWindowStart, chartWindowStart + CHART_WINDOW_SIZE));

  // Get min/max values for current period (with safety check for empty data)
  const minValue = currentData.length > 0 ? Math.min(...currentData.map(item => item.consumption)) : 0;
  const maxValue = currentData.length > 0 ? Math.max(...currentData.map(item => item.consumption)) : 0;

  // Generate load profile stats based on current filter
  const getLoadProfileStats = () => {
    const data = loadProfileData[loadProfilePeriod] || [];

    // Return default values if no data
    if (data.length === 0) {
      return {
        peak: { time: 'N/A', value: '0 kW' },
        offPeak: { time: 'N/A', value: '0 kW' },
        average: { value: '0 kW' }
      };
    }

    const consumption = data.map(item => item.consumption);
    const minIndex = consumption.indexOf(Math.min(...consumption));
    const maxIndex = consumption.indexOf(Math.max(...consumption));
    const average = consumption.reduce((a, b) => a + b, 0) / consumption.length;

    // Use dateRange if available, otherwise fallback to time
    const getPeakTime = (index: number) => {
      return data[index].dateRange || data[index].time;
    };

    // All views now show power in W or kW
    return {
      peak: { time: data[maxIndex].time, value: `${(data[maxIndex].consumption / 1000).toFixed(2)} kW` },
      offPeak: { time: data[minIndex].time, value: `${(data[minIndex].consumption / 1000).toFixed(2)} kW` },
      average: { value: `${(average / 1000).toFixed(2)} kW` }
    };
  };

  const stats = getLoadProfileStats();

  // Export ACTUAL meter readings data (not synthetic)
  const getActualReadingsForExport = () => {
    const readings = rawMeterReadings[loadProfilePeriod];

    return readings.map(reading => {
      const dateTime = new Date(reading.reading_datetime);
      return {
        date: format(dateTime, 'yyyy-MM-dd'),
        time: format(dateTime, 'HH:mm:ss'),
        dayOfWeek: format(dateTime, 'EEEE'),
        power_w: Math.round(Number(reading.instantaneous_power_w)),
        datetime_iso: reading.reading_datetime
      };
    });
  };

  const exportLoadProfile = () => {
    const actualData = getActualReadingsForExport();

    if (actualData.length === 0) {
      toast.error('No data available to export for this period');
      return;
    }

    toast.info(`Exporting ${actualData.length} actual meter readings...`);

    // Create CSV with actual meter readings
    const headers = ['Date', 'Time', 'Day of Week', 'Power (W)', 'ISO Datetime'];
    const rows = actualData.map(item => [
      item.date,
      item.time,
      item.dayOfWeek,
      item.power_w,
      item.datetime_iso
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');

    // Get date range from actual data
    const firstDate = actualData.length > 0 ? actualData[0].date : 'N/A';
    const lastDate = actualData.length > 0 ? actualData[actualData.length - 1].date : 'N/A';

    // Add metadata at the top
    const periodLabels = { day: 'Last 24 Hours', week: 'Last 7 Days', month: 'Last 30 Days' };
    const metadata = [
      `Meter Readings Export - ${periodLabels[loadProfilePeriod]}`,
      `Date Range: ${firstDate} to ${lastDate}`,
      `Total Readings: ${actualData.length}`,
      `Export Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
      '',
      ''
    ].join('\n');

    const fullCsv = metadata + csv;

    const blob = new Blob([fullCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meter-readings-${loadProfilePeriod}-${firstDate}-to-${lastDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);

    toast.success(`Exported ${actualData.length} actual meter readings!`);
  };

  const renderActiveShape = (props: { cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; startAngle: number; endAngle: number; fill: string; payload: { name: string }; percent: number; value: number }) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} className="text-lg">
          {payload.icon}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke="#fff"
          strokeWidth={2}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 10}
          outerRadius={outerRadius + 15}
          fill={fill}
          opacity={0.8}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={2} />
        <circle cx={ex} cy={ey} r={3} fill={fill} stroke="#fff" strokeWidth={1} />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333" className="text-sm font-medium">
          {payload.name}
        </text>
      </g>
    );
  };

  const onPieEnter = (_: unknown, index: number) => {
    setActiveIndex(index);
  };

  const handleRotateChart = () => {
    const newRotation = (pieRotation + 45) % 360;
    setPieRotation(newRotation);
    const sliceAngle = 360 / pieData.length;
    setActiveIndex(Math.floor(newRotation / sliceAngle));
  };

  const CustomDot = (props: { cx: number; cy: number; payload: { consumption: number } }) => {
    const { cx, cy, payload } = props;
    if (payload.consumption === minValue) {
      return <circle cx={cx} cy={cy} r={6} fill="#10B981" stroke="#fff" strokeWidth={2} />;
    }
    if (payload.consumption === maxValue) {
      return <circle cx={cx} cy={cy} r={6} fill="#8B5CF6" stroke="#fff" strokeWidth={2} />;
    }
    return <circle cx={cx} cy={cy} r={4} fill="#3B82F6" stroke="#fff" strokeWidth={2} />;
  };

  return (
    <MainLayout title="Consumption Evolution">
      <div className="p-2 sm:p-4 space-y-4 sm:space-y-6 pb-20 max-w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-center space-x-3 mb-2">
            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />
            <h2 className="text-lg sm:text-xl font-semibold">Energy Consumption Analysis</h2>
          </div>
          <p className="text-blue-100 text-sm sm:text-base">Track your energy usage patterns and optimize consumption</p>
        </div>

        {/* Load Profile */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Load Profile</h3>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              {/* Calendar Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="text-xs sm:text-sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    {format(selectedDate, 'MMM dd, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              
              {/* Period selector */}
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {(['day', 'week', 'month'] as const).map((period) => {
                  const periodColors = {
                    day: 'bg-blue-500',
                    week: 'bg-green-500',
                    month: 'bg-purple-500'
                  };
                  return (
                    <button
                      key={period}
                      onClick={() => setLoadProfilePeriod(period)}
                      className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-none ${
                        loadProfilePeriod === period
                          ? `${periodColors[period]} text-white`
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </button>
                  );
                })}
              </div>
              <Button
                onClick={exportLoadProfile}
                className="flex items-center space-x-2 text-xs sm:text-sm"
                size="sm"
              >
                <Download size={14} />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
          </div>
          

          {/* Load Profile Chart with Navigation */}
          <div className="relative">
            {/* Chart header with unit */}
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Power (Watts)</span>
              {totalDataPoints > CHART_WINDOW_SIZE && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {chartWindowStart + 1}-{Math.min(chartWindowStart + CHART_WINDOW_SIZE, totalDataPoints)} of {totalDataPoints} readings
                </span>
              )}
            </div>

            {/* Loading State - Initial meter lookup */}
            {meterStatus === 'loading' && (
              <div className="h-56 sm:h-72 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Looking up your meter...</p>
                </div>
              </div>
            )}

            {/* Loading State - Fetching data */}
            {meterStatus === 'found' && loading && (
              <div className="h-56 sm:h-72 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading meter data...</p>
                </div>
              </div>
            )}

            {/* No Apartment State */}
            {meterStatus === 'no_apartment' && (
              <div className="h-56 sm:h-72 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center px-4">
                  <AlertCircle className="w-10 h-10 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No apartment assigned</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Your account is not linked to an apartment with a smart meter
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      Please contact your administrator to set up your account
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No Meter State */}
            {meterStatus === 'no_meter' && (
              <div className="h-56 sm:h-72 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center px-4">
                  <AlertCircle className="w-10 h-10 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No smart meter found</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Your apartment doesn't have a smart meter configured
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      Please contact your administrator
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State - No Data for period */}
            {meterStatus === 'found' && !loading && totalDataPoints === 0 && (
              <div className="h-56 sm:h-72 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center px-4">
                  <AlertCircle className="w-10 h-10 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No data available for selected period</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {loadProfilePeriod === 'day'
                        ? `No readings found for ${format(selectedDate, 'MMM dd, yyyy')}`
                        : loadProfilePeriod === 'week'
                        ? `No readings in the past 7 days from ${format(selectedDate, 'MMM dd')}`
                        : `No readings in the past 30 days from ${format(selectedDate, 'MMM dd')}`
                      }
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      Try selecting an earlier date using the calendar picker above
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Chart with Data */}
            {meterStatus === 'found' && !loading && totalDataPoints > 0 && (
              <div className="flex items-center gap-2">
                {/* Left Arrow */}
                <button
                  onClick={handlePrevious}
                  disabled={!canGoBack}
                  className={`p-2 rounded-full transition-colors ${
                    canGoBack
                      ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Chart */}
                <div className="h-56 sm:h-72 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={currentData} margin={{ top: 20, right: 15, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 9, fill: '#9CA3AF' }}
                        tickLine={{ stroke: '#9CA3AF' }}
                        axisLine={{ stroke: '#9CA3AF' }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#9CA3AF' }}
                        tickLine={{ stroke: '#9CA3AF' }}
                        axisLine={{ stroke: '#9CA3AF' }}
                        width={45}
                        domain={currentData.length > 0 ? [
                          (dataMin: number) => Math.max(0, dataMin - (dataMin * 0.2)),
                          (dataMax: number) => dataMax + (dataMax * 0.2)
                        ] : [0, 'auto']}
                        tickFormatter={(value) => `${value}`}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length) return null;
                          const value = payload[0].value as number;
                          return (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                Time: {label}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                Power: <span className="font-semibold text-blue-600 dark:text-blue-400">{value.toLocaleString()} W</span>
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="consumption"
                        stroke={loadProfilePeriod === 'day' ? '#3B82F6' : loadProfilePeriod === 'week' ? '#10B981' : '#8B5CF6'}
                        strokeWidth={2}
                        dot={<CustomDot />}
                        activeDot={{ r: 6, stroke: loadProfilePeriod === 'day' ? '#3B82F6' : loadProfilePeriod === 'week' ? '#10B981' : '#8B5CF6', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Right Arrow */}
                <button
                  onClick={handleNext}
                  disabled={!canGoForward}
                  className={`p-2 rounded-full transition-colors ${
                    canGoForward
                      ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Load profile stats - Responsive with period-matched colors */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className={`rounded-lg p-3 ${
              loadProfilePeriod === 'day' ? 'bg-blue-50 dark:bg-blue-900/20' :
              loadProfilePeriod === 'week' ? 'bg-green-50 dark:bg-green-900/20' :
              'bg-purple-50 dark:bg-purple-900/20'
            }`}>
              <h4 className={`font-medium text-sm ${
                loadProfilePeriod === 'day' ? 'text-blue-900 dark:text-blue-300' :
                loadProfilePeriod === 'week' ? 'text-green-900 dark:text-green-300' :
                'text-purple-900 dark:text-purple-300'
              }`}>
                Peak {loadProfilePeriod === 'day' ? 'Time' : loadProfilePeriod === 'week' ? 'Period' : 'Day'}
              </h4>
              <p className={`text-lg font-bold ${
                loadProfilePeriod === 'day' ? 'text-blue-600 dark:text-blue-400' :
                loadProfilePeriod === 'week' ? 'text-green-600 dark:text-green-400' :
                'text-purple-600 dark:text-purple-400'
              }`}>{stats.peak.time}</p>
              <p className={`text-xs ${
                loadProfilePeriod === 'day' ? 'text-blue-700 dark:text-blue-300' :
                loadProfilePeriod === 'week' ? 'text-green-700 dark:text-green-300' :
                'text-purple-700 dark:text-purple-300'
              }`}>{stats.peak.value}</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
              <h4 className="font-medium text-amber-900 dark:text-amber-300 text-sm">
                Off-Peak {loadProfilePeriod === 'day' ? 'Time' : loadProfilePeriod === 'week' ? 'Period' : 'Day'}
              </h4>
              <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{stats.offPeak.time}</p>
              <p className="text-xs text-amber-700 dark:text-amber-300">{stats.offPeak.value}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <h4 className="font-medium text-gray-900 dark:text-gray-300 text-sm">Average Load</h4>
              <p className="text-lg font-bold text-gray-700 dark:text-gray-200">{stats.average.value}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{loadProfilePeriod === 'day' ? 'Per reading' : loadProfilePeriod === 'week' ? 'Per 4h interval' : 'Per day'} average</p>
            </div>
          </div>
        </div>

        {/* Main Chart - Shows consumption by selected period */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
          <ConsumptionChart
            showControls={true}
            height="h-64 sm:h-80"
            showPriceLabels={true}
            realData={consumptionByPeriod[chartPeriod]}
            pricePerKwh={ratePerKwh}
            externalPeriod={chartPeriod}
            onPeriodChange={(period) => setChartPeriod(period)}
          />
          {consumptionByPeriod[chartPeriod].length > 0 && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
              Showing {consumptionByPeriod[chartPeriod].length} {chartPeriod === 'day' ? 'days' : chartPeriod === 'week' ? 'weeks' : chartPeriod === 'month' ? 'months' : 'years'} of data
            </div>
          )}
        </div>

        {/* Enhanced 3D Pie Chart - Mobile optimized - TEMPORARILY DISABLED
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Energy Distribution</h3>
            </div>
            <Button
              onClick={handleRotateChart}
              className="flex items-center justify-center space-x-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 hover:from-yellow-500 hover:to-orange-600 text-sm"
              size="sm"
            >
              <Zap className="w-4 h-4" />
              <span>Rotate Chart</span>
            </Button>
          </div>

          <div className="flex flex-col space-y-6">
            <div className="w-full">
              <div className="h-64 sm:h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      activeIndex={activeIndex}
                      activeShape={renderActiveShape}
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={window.innerWidth < 640 ? 80 : 120}
                      innerRadius={window.innerWidth < 640 ? 40 : 60}
                      startAngle={pieRotation}
                      endAngle={pieRotation + 360}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                      onMouseEnter={onPieEnter}
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          style={{
                            filter: index === activeIndex ? 'brightness(1.2) drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'brightness(1)',
                            transformOrigin: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string, props: { payload: { description: string } }) => [
                        `${value}%`,
                        `${props.payload.description}`
                      ]}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        fontSize: '12px'
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="w-full space-y-2">
              {pieData.map((entry, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-3 p-3 rounded-lg transition-all cursor-pointer border text-sm ${
                    index === activeIndex
                      ? 'bg-gray-50 dark:bg-gray-700 scale-105 shadow-md border-2'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                  style={{ borderColor: index === activeIndex ? entry.color : undefined }}
                  onClick={() => setActiveIndex(index)}
                >
                   <div className="text-center min-w-[2.5rem] flex-shrink-0">
                     <div className="text-xs font-mono text-gray-600 dark:text-gray-400">{entry.pattern}</div>
                     <div className="text-lg">{entry.icon}</div>
                   </div>
                   <div className="flex-1 min-w-0">
                     <p className="font-semibold text-gray-900 dark:text-white truncate text-sm">{entry.name}</p>
                     <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{entry.description}</p>
                   </div>
                   <div className="text-right flex-shrink-0">
                     <p className="text-base sm:text-lg font-bold" style={{ color: entry.color }}>
                       {entry.value}%
                     </p>
                   </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        */}

        {/* Statistics - Mobile optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Average Daily</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {consumptionStats.averageDaily > 0 ? `${consumptionStats.averageDaily.toFixed(1)} kWh` : '-- kWh'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">This Month</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {consumptionStats.thisMonth > 0 ? `${consumptionStats.thisMonth.toFixed(0)} kWh` : '-- kWh'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">vs Last Month</p>
                <p className={`text-lg sm:text-xl font-bold ${consumptionStats.vsLastMonth < 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {consumptionStats.vsLastMonth !== 0 ? `${consumptionStats.vsLastMonth > 0 ? '+' : ''}${consumptionStats.vsLastMonth.toFixed(1)}%` : '--'}
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </MainLayout>
  );
};

export default ConsumptionEvolution;
